import { useEffect, useState } from "react";
import { Plus } from "lucide-react";

type Action = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
};

type FloatingActionButtonProps = {
  actions: Action[];
  localStorageKey?: string;
  visible?: boolean;
  autoExpandDelay?: number;
  autoExpandDuration?: number;
  disableAutoExpandStorage?: boolean;
};

export default function FloatingActionButton({
  actions,
  localStorageKey = "seen_fab",
  visible = true,
  autoExpandDelay = 2000,
  autoExpandDuration = 3000,
  disableAutoExpandStorage = true,
}: FloatingActionButtonProps) {
  const [open, setOpen] = useState(false);
  const [expandedOnce, setExpandedOnce] = useState(false);
  const multiple = actions.length > 1;

  useEffect(() => {
    if (!visible || multiple) return;

    const seen = localStorage.getItem(localStorageKey);
    if (seen && !disableAutoExpandStorage) return;

    let delayTimer: NodeJS.Timeout;
    let collapseTimer: NodeJS.Timeout;

    delayTimer = setTimeout(() => {
      setExpandedOnce(true);

      collapseTimer = setTimeout(() => {
        setExpandedOnce(false);
        if (!disableAutoExpandStorage) {
          localStorage.setItem(localStorageKey, "true");
        }
      }, autoExpandDuration);
    }, autoExpandDelay);

    return () => {
      clearTimeout(delayTimer);
      clearTimeout(collapseTimer);
    };
  }, [
    visible,
    multiple,
    localStorageKey,
    autoExpandDelay,
    autoExpandDuration,
    disableAutoExpandStorage,
  ]);

  return (
    <div className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end gap-2">
      {/* Wenn mehrere Aktionen: gestapelte grüne Buttons mit Icon + Label */}
      {multiple &&
        open &&
        actions.map((action, index) => (
          <button
            key={index}
            onClick={() => {
              setOpen(false);
              action.onClick();
            }}
            className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-full shadow-md flex items-center gap-2 text-sm"
          >
            <span className="text-xl">+</span>
            {action.icon}
            <span>{action.label}</span>
          </button>
        ))}

      {/* Haupt-Button */}
      <button
        onClick={() => {
          if (!multiple) {
            actions[0].onClick();
          } else {
            setOpen((prev) => !prev);
          }
        }}
        className={`rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-lg flex items-center justify-center transform transition-all duration-700 ease-in-out
            ${
              expandedOnce
                ? "w-auto px-5 py-2.5 scale-105"
                : "w-14 h-14 text-2xl scale-100"
            }
        `}
        aria-label="Hinzufügen"
      >
        <Plus className={expandedOnce ? "mr-2" : ""} />
        {expandedOnce && !multiple && (
          <span className="text-sm">{actions[0].label}</span>
        )}
      </button>
    </div>
  );
}
